import { useRef, useEffect, useCallback } from "react";
import { usePaneStore } from "../state/store-context";
import { useCycleSnippetZoomPersist } from "../hooks/useCycleSnippetZoomPersist";
import type { BoundingBox } from "../state/types";

const ZOOM_LABELS: Record<number, string> = {
  0: "Auto",
  1: "1x",
  2: "2x",
  3: "3x",
};

/**
 * Draws a cropped snippet of the document page around the given bounding box.
 * Bounding box coordinates are normalized (0-1 relative to page dimensions).
 */
export function drawSnippet(
  canvas: HTMLCanvasElement,
  bitmap: ImageBitmap,
  bbox: BoundingBox,
  zoom: number,
  canvasWidth: number,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const padding = 0.2;

  // Convert normalized bbox to pixel coordinates
  const bboxPxX = bbox.x * bitmap.width;
  const bboxPxY = bbox.y * bitmap.height;
  const bboxPxW = bbox.width * bitmap.width;
  const bboxPxH = bbox.height * bitmap.height;

  // Source region with padding
  const srcX = Math.max(0, bboxPxX - bboxPxW * padding);
  const srcY = Math.max(0, bboxPxY - bboxPxH * padding);
  const srcW = Math.min(bitmap.width - srcX, bboxPxW * (1 + padding * 2));
  const srcH = Math.min(bitmap.height - srcY, bboxPxH * (1 + padding * 2));

  if (srcW <= 0 || srcH <= 0) return;

  // Calculate effective zoom
  const effectiveZoom = zoom === 0 ? canvasWidth / srcW : zoom;

  const destW = srcW * effectiveZoom;
  const destH = srcH * effectiveZoom;

  canvas.width = destW;
  canvas.height = destH;

  // Clear
  ctx.clearRect(0, 0, destW, destH);

  // Draw cropped region
  ctx.drawImage(bitmap, srcX, srcY, srcW, srcH, 0, 0, destW, destH);

  // Draw bounding box indicator
  ctx.strokeStyle = "#ea580c";
  ctx.lineWidth = 2;
  const indicatorX = (bboxPxX - srcX) * effectiveZoom;
  const indicatorY = (bboxPxY - srcY) * effectiveZoom;
  ctx.strokeRect(indicatorX, indicatorY, bboxPxW * effectiveZoom, bboxPxH * effectiveZoom);
}

/** Check whether any field in the array has a bounding box. */
export function hasAnyBoundingBox(fields: Array<{ boundingBox: BoundingBox | null }>): boolean {
  return fields.some((f) => f.boundingBox !== null);
}

export function SnippetPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const activeFieldIndex = usePaneStore((s) => s.activeFieldIndex);
  const fields = usePaneStore((s) => s.fields);
  const pages = usePaneStore((s) => s.pages);
  const snippetZoom = usePaneStore((s) => s.snippetZoom);
  const snippetVisible = usePaneStore((s) => s.snippetVisible);
  // E-049 — cycle + persist (was the raw store action; the cycled choice now
  // writes snippetZoomDefault so it survives doc/batch/session transitions).
  const cycleSnippetZoom = useCycleSnippetZoomPersist();
  const toggleSnippetPanel = usePaneStore((s) => s.toggleSnippetPanel);

  const activeField = fields[activeFieldIndex] ?? null;
  const bbox = activeField?.boundingBox ?? null;
  const pageIndex = bbox ? bbox.pageNumber - 1 : -1;
  const page = pageIndex >= 0 ? pages[pageIndex] : null;
  const bitmap = page?.imageBitmap ?? null;

  // Hide entirely if no fields have bounding boxes
  const anyBbox = hasAnyBoundingBox(fields);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bitmap || !bbox) return;

    const container = canvas.parentElement;
    const canvasWidth = container ? container.clientWidth : 300;
    drawSnippet(canvas, bitmap, bbox, snippetZoom, canvasWidth);
  }, [bitmap, bbox, snippetZoom]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (!anyBbox) return null;

  return (
    <div className="snippet-panel" data-testid="snippet-panel">
      <div className="snippet-header">
        <span className="snippet-title">Snippet</span>
        <div className="snippet-controls">
          <button
            className="snippet-zoom-btn"
            onClick={cycleSnippetZoom}
            title="Cycle zoom (Ctrl+; or F2)"
            data-testid="snippet-zoom-btn"
          >
            {ZOOM_LABELS[snippetZoom] ?? `${snippetZoom}x`}
          </button>
          <button
            className="snippet-toggle-btn"
            onClick={toggleSnippetPanel}
            title={snippetVisible ? "Collapse snippet" : "Expand snippet"}
            data-testid="snippet-toggle-btn"
          >
            {snippetVisible ? "\u25B4" : "\u25BE"}
          </button>
        </div>
      </div>

      {snippetVisible && (
        <div className="snippet-body">
          {!bbox && (
            <div className="snippet-placeholder" data-testid="snippet-placeholder">
              No region data for this field
            </div>
          )}
          {bbox && !bitmap && (
            <div className="snippet-loading" data-testid="snippet-loading">
              <div className="snippet-spinner" />
            </div>
          )}
          {bbox && bitmap && (
            <canvas
              ref={canvasRef}
              className="snippet-canvas"
              data-testid="snippet-canvas"
            />
          )}
        </div>
      )}

      <style>{`
        .snippet-panel {
          border-bottom: 1px solid var(--color-border);
          background: var(--color-bg-secondary);
        }
        .snippet-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-1) var(--space-2);
          font-size: 0.7rem;
          color: var(--color-text-muted);
          border-bottom: 1px solid var(--color-border);
        }
        .snippet-title {
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .snippet-controls {
          display: flex;
          gap: var(--space-1);
          align-items: center;
        }
        .snippet-zoom-btn,
        .snippet-toggle-btn {
          border: none;
          background: transparent;
          color: var(--color-text-secondary);
          font-size: 0.7rem;
          cursor: pointer;
          padding: 2px 6px;
          border-radius: var(--radius-sm);
        }
        .snippet-zoom-btn:hover,
        .snippet-toggle-btn:hover {
          background: var(--color-bg-elevated);
        }
        /* F-090 — fixed-height viewport so the panel never jumps per field.
           Wide crops scale to the viewport height and scroll horizontally
           INSIDE the body (the container scrolls, never the page); they were
           previously width-shrunk (max-width: 100%) into a sliver. Canvas
           centering uses auto margins, not justify-content — flex centering
           clips the left edge of overflowing content. */
        .snippet-body {
          height: 200px;
          overflow-x: auto;
          overflow-y: hidden;
          display: flex;
          align-items: center;
        }
        .snippet-canvas {
          display: block;
          max-height: 100%;
          margin: auto;
          object-fit: contain;
        }
        .snippet-placeholder,
        .snippet-loading {
          padding: var(--space-4);
          font-size: 0.75rem;
          color: var(--color-text-muted);
          text-align: center;
        }
        .snippet-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid var(--color-border);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
